"use client";

import { ArrowRight, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const reviews = [{
  id: 1,
  initials: "SM",
  name: "Sarah M.",
  date: "[2024-08-15]",
  quote: "The team at Tiny to Teen is incredibly professional and compassionate. They made a difficult transition feel safe for our children and provided much-needed peace of mind."
}, {
  id: 2,
  initials: "JT",
  name: "James T.",
  date: "[2024-07-22]",
  quote: "Highly recommend their monitored exchange services. Neutral, firm on boundaries, and always child-centered. They truly prioritize the well-being of the kids."
}, {
  id: 3,
  initials: "LG",
  name: "Linda G.",
  date: "[2024-06-10]",
  quote: "Thorough reporting and a very calm environment. They truly understand the complexities of court-involved families and provide objective, professional observations."
}, {
  id: 4,
  initials: "RH",
  name: "Robert H.",
  date: "[2024-05-04]",
  quote: "Professionalism at its finest. They provide the structure needed for safe parenting time, ensuring that every interaction is respectful and follows court guidelines."
}, {
  id: 5,
  initials: "MS",
  name: "Maria S.",
  date: "[2024-04-18]",
  quote: "A vital resource for Arizona families. Their trauma-informed approach sets them apart, making a stressful situation much more manageable for everyone involved."
}, {
  id: 6,
  initials: "DL",
  name: "David L.",
  date: "[2024-03-29]",
  quote: "Safe, neutral, and reliable. We are grateful for the support Tiny to Teen has provided our family during our reunification process. Highly recommended."
}];
export default function Voices() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const [api, setApi] = useState<CarouselApi>();
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  useEffect(() => {
    if (!api) return;
    const update = () => {
      setCanPrev(api.canScrollPrev());
      setCanNext(api.canScrollNext());
    };
    update();
    api.on("select", update);
    api.on("reInit", update);
    return () => {
      api.off("select", update);
    };
  }, [api]);
  return <section data-section-id="1768" id="voices-of-support" className="bg-background text-foreground relative overflow-hidden">
      {}
      <div className="h-6 w-full" style={{
      backgroundImage: "repeating-linear-gradient(45deg, hsl(34 32% 70% / 0.1) 0 2px, transparent 2px 6px), repeating-linear-gradient(-45deg, hsl(34 32% 70% / 0.1) 0 2px, transparent 2px 6px)",
      backgroundColor: "var(--background)"
    }} aria-hidden="true" />

      <div ref={ref} className="container mx-auto max-w-[1360px] px-6 md:px-8 py-28 md:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-4">
            <div className={`lg:sticky lg:top-24 transition-all duration-500 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              {}
              <div className="flex items-center gap-3 mb-8">
                <span className="inline-block w-2.5 h-2.5" style={{
                backgroundColor: "var(--primary)"
              }} aria-hidden="true" />
                <span className="font-mono text-xs font-semibold uppercase" style={{
                color: "var(--primary)",
                letterSpacing: "0.15em"
              }}>
                  § 05 — Voices of Support
                </span>
              </div>

              {}
              <h2 className="font-default font-normal uppercase text-foreground" style={{
              fontSize: "clamp(2.5rem, 5.4vw, 5rem)",
              lineHeight: "1.1",
              letterSpacing: "-0.02em"
            }}>
                Families{" "}
                <span className="italic" style={{
                color: "var(--primary)"
              }}>
                  Trust
                </span>{" "}
                Our Neutrality.
              </h2>

              {}
              <div className="mt-10 space-y-4">
                <div className="flex items-center gap-3">
                  <svg viewBox="0 0 48 48" className="w-7 h-7" aria-hidden="true">
                    <path fill="currentColor" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 19.5-8 19.5-20 0-1.3-.1-2.4-.4-3.5z" />
                  </svg>
                  <span className="font-default font-bold uppercase text-foreground text-lg tracking-wide">
                    Client Feedback
                  </span>
                </div>

                <div className="space-y-3 pt-2">
                  <div className="flex items-center gap-3">
                    <span className="inline-block w-2 h-2" style={{
                    backgroundColor: "var(--primary)"
                  }} aria-hidden="true" />
                    <span className="font-mono text-sm uppercase text-foreground" style={{
                    letterSpacing: "0.12em"
                  }}>
                      Arizona-Based Service
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="inline-block w-2 h-2" style={{
                    backgroundColor: "var(--primary)"
                  }} aria-hidden="true" />
                    <span className="font-mono text-sm uppercase text-foreground" style={{
                    letterSpacing: "0.12em"
                  }}>
                      Trauma-Informed Care
                    </span>
                  </div>
                </div>

                {}
                <div className="pt-6">
                  <Link to="/ContactPage" className="group inline-flex items-center gap-2 font-mono text-sm uppercase text-foreground hover:text-primary transition-colors duration-200" style={{
                  letterSpacing: "0.12em"
                }}>
                    <span>Inquire Today</span>
                    <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" style={{
                    color: "var(--primary)"
                  }} />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-8 min-w-0">
            <div className={`transition-all duration-500 ease-out delay-150 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <Carousel opts={{
              align: "start",
              loop: false
            }} setApi={setApi} className="w-full">
                <CarouselContent className="-ml-4">
                  {reviews.map((review, i) => <CarouselItem key={review.id} data-index={i} className="pl-4 basis-full md:basis-1/2 lg:basis-[71%] xl:basis-[55%]">
                      <article className="relative h-full p-8 border-l-4" style={{
                    backgroundColor: "var(--card)",
                    borderColor: "var(--primary)",
                    color: "var(--card-foreground)",
                    minHeight: "320px"
                  }}>
                        <div className="flex flex-col h-full">
                          {}
                          <div className="flex items-center gap-1" aria-label="5 out of 5 stars">
                            {[0, 1, 2, 3, 4].map(s => <Star key={s} className="w-[18px] h-[18px]" style={{
                          color: "var(--primary)",
                          fill: "var(--primary)"
                        }} strokeWidth={1.5} />)}
                          </div>

                          {}
                          <p className="font-default mt-6 text-lg leading-relaxed flex-1" style={{
                        letterSpacing: "0.01em"
                      }}>
                            &ldquo;{review.quote}&rdquo;
                          </p>

                          {}
                          <div className="h-px w-full my-6" style={{
                        backgroundColor: "var(--secondary)"
                      }} aria-hidden="true" />

                          {}
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="flex items-center justify-center w-10 h-10 shrink-0" style={{
                            backgroundColor: "var(--sage-deep)",
                            color: "white"
                          }}>
                                <span className="font-sans font-bold text-sm uppercase" style={{
                              letterSpacing: "0.02em"
                            }}>
                                  {review.initials}
                                </span>
                              </div>
                              <span className="font-default font-bold text-sm truncate">
                                {review.name}
                              </span>
                            </div>
                            <span className="font-mono text-xs shrink-0" style={{
                          letterSpacing: "0.08em"
                        }}>
                              {review.date}
                            </span>
                          </div>
                        </div>
                      </article>
                    </CarouselItem>)}
                </CarouselContent>
              </Carousel>

              {}
              <div className="flex items-center justify-end gap-3 mt-10">
                <Button variant="outline" onClick={() => api?.scrollPrev()} disabled={!canPrev} aria-label="Previous review" className="w-14 h-14 p-0 border bg-transparent hover:bg-primary hover:text-white disabled:opacity-30 transition-colors duration-200" style={{
                borderColor: "var(--secondary)",
                color: "var(--secondary)"
              }}>
                  <ChevronLeft className="w-6 h-6" strokeWidth={2} />
                </Button>
                <Button variant="outline" onClick={() => api?.scrollNext()} disabled={!canNext} aria-label="Next review" className="w-14 h-14 p-0 border bg-transparent hover:bg-primary hover:text-white disabled:opacity-30 transition-colors duration-200" style={{
                borderColor: "var(--secondary)",
                color: "var(--secondary)"
              }}>
                  <ChevronRight className="w-6 h-6" strokeWidth={2} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}